import {
  ActionName,
  CategoryFieldName,
  CategoryName,
  DisplayModeName,
  resolveButtonedName,
  TFormName,
  TViewName,
} from '@infotech-soe/metaschema';

import IStoredSchemaNameManager, {
  StoredSchemaName,
} from './IStoredSchemaNameManager';
import StoredSchemaNameParserDelegate from './StoredSchemaNameParserDelegate';

const APPLICATION = 'application';
const FIELDS = 'fields';
const ACTIONS = 'actions';
const CATEGORIES = 'categories';
const DISPLAY_MODES = 'displayModes';
const DOMAINS = 'domains';
const DOMAINS_LIST = 'DOMAINS.SOURCES';
const FORMS = 'forms';
const MENUS = 'menus';
const VIEWS = 'views';
const TEMPLATES = 'templates';
const CATEGORY_ACTIONS = 'categoryActions';
const CONDITIONS = 'conditions';

export const DOMAINS_RESOLVED = 'DOMAINS.RESOLVED';

class DefaultStoredSchemaNameManager implements IStoredSchemaNameManager {
  buildResolvedDomainsList(): string {
    return DOMAINS_RESOLVED;
  }
  public buildActionName(
    category: CategoryName,
    action: ActionName
  ): StoredSchemaName {
    return `${ACTIONS}/${category}/${action}`;
  }

  public buildCategoryName(category: CategoryName): StoredSchemaName {
    return `${CATEGORIES}/${category}`;
  }

  public buildCategoryActionsName(category: CategoryName): StoredSchemaName {
    return `${CATEGORY_ACTIONS}/${category}`;
  }

  public buildDisplayModeName(
    category: CategoryName,
    displayMode: DisplayModeName
  ): StoredSchemaName {
    return `${DISPLAY_MODES}/${category}/${displayMode}`;
  }

  public buildDomainsName(schema: string): StoredSchemaName {
    return `${DOMAINS}/${schema}`;
  }

  public buildDomainSourcesName(): StoredSchemaName {
    return DOMAINS_LIST;
  }

  public buildFormName(
    category: CategoryName,
    form: TFormName
  ): StoredSchemaName {
    const { category: resolvedCategory, name: resolvedName } =
      resolveButtonedName(form);
    return `${FORMS}/${resolvedCategory ?? category}/${resolvedName}`;
  }

  public buildApplicationName(application: string): StoredSchemaName {
    return `${APPLICATION}/${application}`;
  }

  public buildMenuName(application: string): StoredSchemaName {
    return `${MENUS}/${application}`;
  }

  public buildViewName(view: TViewName): StoredSchemaName {
    return `${VIEWS}/${view}`;
  }

  public buildTemplateName(kind: string, name: string): StoredSchemaName {
    return `${TEMPLATES}/${kind}/${name}`;
  }

  public buildConditionsSetName(
    category: CategoryName,
    name: string
  ): StoredSchemaName {
    return `${CONDITIONS}/${category}/${name}`;
  }

  public buildFieldName(
    category: CategoryName,
    name: CategoryFieldName
  ): StoredSchemaName {
    return `${FIELDS}/${category}/${name}`;
  }

  public parseSchemaName<T>(
    name: StoredSchemaName,
    delegate: StoredSchemaNameParserDelegate<T>
  ): T {
    const nameParts = name.split('/');
    const directory = nameParts[0];

    switch (directory) {
      case APPLICATION:
        return delegate.onApplication(nameParts[1]);

      case DOMAINS:
        return delegate.onDomains(nameParts[1]);

      case DOMAINS_LIST:
        return delegate.onDomainsList();

      case CATEGORIES:
        return delegate.onCategory(nameParts[1]);

      case CATEGORY_ACTIONS:
        return delegate.onCategoryActions(nameParts[1]);

      case CONDITIONS:
        return delegate.onConditionsSet(nameParts[2]);

      case VIEWS:
        return delegate.onView(nameParts[1]);

      case FORMS:
        return delegate.onForm(nameParts[1], nameParts[2]);

      case ACTIONS:
        return delegate.onAction(nameParts[1], nameParts[2]);

      case DISPLAY_MODES:
        return delegate.onDisplayMode(nameParts[1], nameParts[2]);

      case MENUS:
        return delegate.onMenu(nameParts[1]);

      case TEMPLATES:
        return delegate.onTemplate(nameParts[1], nameParts[2]);

      case FIELDS:
        return delegate.onFields(nameParts[1], nameParts[2]);

      default:
        throw new Error('Unknown schema directory: ' + directory);
    }
  }
}

export default DefaultStoredSchemaNameManager;
